import {useEffect, useRef, useState, useCallback} from 'react';
import {proyectoBloqueoApi} from '../api/api';
import {AUTH_TOKEN_STORAGE_KEY} from '../api/config';

interface UseBloqueoProyectoResult {
    permitido: boolean;
    cargando: boolean;
    nombreUsuario: string | null;
    bloqueadoDesde: Date | null;
    intentarTomarControl: () => Promise<boolean>;
}

export function useBloqueoProyecto(
    proyectoId?: number,
    usuarioId?: number
): UseBloqueoProyectoResult {
    const [permitido, setPermitido] = useState(false);
    const [cargando, setCargando] = useState(true);
    const [nombreUsuario, setNombreUsuario] = useState<string | null>(null);
    const [bloqueadoDesde, setBloqueadoDesde] = useState<Date | null>(null);

    const heartbeatRef = useRef<number | null>(null);
    
    const detenerHeartbeat = () => {
        if (heartbeatRef.current) {
            clearInterval(heartbeatRef.current);
            heartbeatRef.current = null;
        }
    };

    const liberar = useCallback(() => {
        if (!proyectoId || !usuarioId) return;

        detenerHeartbeat();

        try {
            const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
            const url = `/api/proyectos/${proyectoId}/Bloqueo/${usuarioId}`;
            
            fetch(url, {
                method: 'DELETE',
                headers: {
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                },
                keepalive: true
            }).catch(e => console.warn('Error liberando bloqueo en background', e));

        } catch (e) {
            console.warn('Error general liberando bloqueo', e);
        }
    }, [proyectoId, usuarioId]);

    const intentarTomarControl = useCallback(async () => {
        if (!proyectoId || !usuarioId) return false;

        detenerHeartbeat(); // Detenemos cualquier heartbeat anterior

        try {
            const res = await proyectoBloqueoApi.bloquear(proyectoId, usuarioId);

            if (res.exito) {
                setPermitido(true);
                setNombreUsuario(null);
                setBloqueadoDesde(null);
                
                // Reiniciamos el heartbeat
                heartbeatRef.current = window.setInterval(async () => {
                    try {
                        await proyectoBloqueoApi.heartbeat(proyectoId, usuarioId);
                    } catch (e) {
                        console.warn('Heartbeat fallo', e);
                    }
                }, 30000);

                return true;
            } else {
                setPermitido(false);
                setNombreUsuario(res.nombreUsuario ?? null);
                setBloqueadoDesde(
                    res.fechaInicioBloqueo ? new Date(res.fechaInicioBloqueo) : null
                );
                return false;
            }
        } catch (e) {
            console.error('Error tomando control', e);
            return false;
        }
    }, [proyectoId, usuarioId]);

    useEffect(() => {
        if (!proyectoId || !usuarioId) {
            setCargando(false);
            setPermitido(false);
            return;
        }

        let isMounted = true;
        setCargando(true);

        const inicializar = async () => {
            try {
                const res = await proyectoBloqueoApi.bloquear(proyectoId, usuarioId);
                if (!isMounted) return;

                if (res.exito) {
                    setPermitido(true);
                    setNombreUsuario(null);
                    setBloqueadoDesde(null);
                    
                    detenerHeartbeat(); // Limpiamos por si acaso
                    heartbeatRef.current = window.setInterval(async () => {
                        try {
                            await proyectoBloqueoApi.heartbeat(proyectoId, usuarioId);
                        } catch (e) {
                            console.warn('Heartbeat fallo', e);
                        }
                    }, 30000);

                } else {
                    setPermitido(false);
                    setNombreUsuario(res.nombreUsuario ?? null);
                    setBloqueadoDesde(
                        res.fechaInicioBloqueo ? new Date(res.fechaInicioBloqueo) : null
                    );
                }
            } catch (e) {
                console.error('Error bloqueando proyecto', e);
                if (isMounted) {
                    setPermitido(false);
                }
            } finally {
                if (isMounted) {
                    setCargando(false);
                }
            }
        };

        inicializar();

        // Liberar al cerrar/recargar la pestaña
        window.addEventListener('beforeunload', liberar);

        return () => {
            isMounted = false;
            liberar();
            window.removeEventListener('beforeunload', liberar);
        };
    }, [proyectoId, usuarioId, liberar]);

    return {
        permitido,
        cargando,
        nombreUsuario,
        bloqueadoDesde,
        intentarTomarControl,
    };
}